import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";

const blank = { kind: "parent", email: "", name: "", phone: "", role: "teacher", relationship: "Mother", permission_level: "full",
                access: {}, student_ids: [], expires_in_days: "", message: "", test: false,
                legal_guardian: null, pickup_authorized: null, billing_responsible: null };

/** Staff-initiated invitation for a parent/guardian (connected to children) or a staff member. */
export const InviteDialog = ({ open, onOpenChange, preselect = [], onSent }) => {
  const [meta, setMeta] = useState(null);
  const [kids, setKids] = useState([]);
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({ ...blank, student_ids: preselect });
    Promise.all([api.get("/invitations/meta"), api.get("/students")])
      .then(([m, s]) => { setMeta(m.data); setKids(s.data); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [open, preselect]);

  const level = meta?.permission_levels.find((l) => l.key === f.permission_level);
  const toggleKid = (id) => setF({ ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter((x) => x !== id) : [...f.student_ids, id] });
  const flag = (k) => (f[k] === null ? Boolean(level?.[{ legal_guardian: "legal", pickup_authorized: "pickup", billing_responsible: "billing" }[k]]) : f[k]);

  const send = async () => {
    if (!f.email.trim() || !f.name.trim()) return toast.error("Name and email are required");
    if (f.kind === "parent" && f.student_ids.length === 0) return toast.error("Choose at least one child");
    setBusy(true);
    try {
      const payload = { email: f.email.trim(), name: f.name.trim(), phone: f.phone.trim(), message: f.message, test: f.test,
                        expires_in_days: f.expires_in_days ? Number(f.expires_in_days) : null,
                        role: f.kind === "parent" ? "parent" : f.role,
                        relationship: f.relationship, permission_level: f.permission_level, access: f.access,
                        student_ids: f.kind === "parent" ? f.student_ids : [],
                        legal_guardian: flag("legal_guardian"), pickup_authorized: flag("pickup_authorized"), billing_responsible: flag("billing_responsible") };
      const { data } = await api.post("/invitations", payload);
      toast.success(data.send?.result === "sent" ? `Invitation emailed to ${data.email}` : `Invitation created — email ${data.send?.result?.replace(/_/g, " ")}`);
      onOpenChange(false);
      onSent?.(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Send an invitation</DialogTitle>
          <DialogDescription>The person receives a secure one-time link. Their account inherits only what you approve here.</DialogDescription>
        </DialogHeader>
        {!meta ? <p className="text-muted-foreground">Loading…</p> : (
          <div className="space-y-5">
            <div className="flex gap-2" role="tablist">
              {[["parent", "Parent / guardian"], ["staff", "Staff member"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} data-testid={`invite-kind-${k}`}
                        className={`px-4 py-2 rounded-full text-sm font-bold ${f.kind === k ? "bg-[#3B82F6] text-white" : "bg-white border border-border"}`}>{l}</button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm font-bold">Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid="invite-field-name" /></div>
              <div><Label className="text-sm font-bold">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid="invite-field-email" /></div>
              <div><Label className="text-sm font-bold">Phone (optional)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid="invite-field-phone" /></div>
              <div><Label className="text-sm font-bold">Link expires in (days)</Label><Input type="number" min={1} max={60} placeholder={String(meta.default_expiry_days)} value={f.expires_in_days} onChange={(e) => setF({ ...f, expires_in_days: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid="invite-field-expiry" /></div>
            </div>

            {f.kind === "parent" ? (
              <>
                <div>
                  <Label className="text-sm font-bold">Children to connect</Label>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="invite-children">
                    {kids.map((k) => (
                      <label key={k.id} className={`text-sm font-semibold flex items-center gap-2 px-3 py-2 rounded-full border cursor-pointer ${f.student_ids.includes(k.id) ? "border-[#3B82F6] bg-blue-50" : "border-border"}`}>
                        <input type="checkbox" checked={f.student_ids.includes(k.id)} onChange={() => toggleKid(k.id)} data-testid={`invite-child-${k.id}`} />
                        {k.first_name} {k.last_name} <span className="text-xs text-muted-foreground">{k.room}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-bold">Relationship to the child</Label>
                  <Select value={f.relationship} onValueChange={(v) => setF({ ...f, relationship: v })}>
                    <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="invite-relationship"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.relationships.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-bold">Access level</Label>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="invite-levels">
                    {meta.permission_levels.map((l) => (
                      <button key={l.key} type="button" onClick={() => setF({ ...f, permission_level: l.key, legal_guardian: null, pickup_authorized: null, billing_responsible: null })}
                              data-testid={`invite-level-${l.key}`}
                              className={`text-left rounded-2xl border p-3 ${f.permission_level === l.key ? "border-[#3B82F6] bg-blue-50" : "border-border bg-white"}`}>
                        <p className="font-bold text-sm">{l.label}</p><p className="text-xs text-muted-foreground mt-1">{l.description}</p>
                      </button>
                    ))}
                  </div>
                  {f.permission_level === "custom" && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="invite-custom-access">
                      {meta.access_keys.map((a) => (
                        <label key={a.key} className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border">
                          <input type="checkbox" checked={Boolean(f.access[a.key])} onChange={(e) => setF({ ...f, access: { ...f.access, [a.key]: e.target.checked } })} data-testid={`invite-access-${a.key}`} /> {a.label}
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[["legal_guardian", "Legal guardian"], ["pickup_authorized", "Authorized for pickup"], ["billing_responsible", "Billing contact"]].map(([k, l]) => (
                      <label key={k} className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border">
                        <input type="checkbox" checked={flag(k)} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`invite-flag-${k}`} /> {l}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div>
                <Label className="text-sm font-bold">Role</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.staff_roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="mt-2 text-xs text-muted-foreground">Only a super administrator can invite administrators. The person never chooses their own role.</p>
              </div>
            )}

            <div><Label className="text-sm font-bold">Personal note (optional)</Label>
              <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} maxLength={500} className="mt-2 rounded-xl" data-testid="invite-message-field" /></div>
            <label className="text-xs font-semibold flex items-center gap-2"><input type="checkbox" checked={f.test} onChange={(e) => setF({ ...f, test: e.target.checked })} data-testid="invite-test-flag" /> Mark as a test invitation (safe to delete later)</label>
            <Button onClick={send} disabled={busy} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="invite-send-btn">
              <PaperPlaneTilt size={18} className="mr-2" /> {busy ? "Sending…" : "Send invitation"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
